<template>
  <section class="company-filter" v-if="filterOptions.companies && filterOptions.companies.length">
    <div class="company-logo">
      <img class="logo" v-if="currCompanyLogo" :src="currCompanyLogo" />
    </div>

    <template v-if="isUserLevelBuzzz">
      <div class="select-seperator"></div>
      <Select
        @input="handleFranchiseSelect($event)"
        class="franchise-select"
        :placeholder="$t('franchise')"
        :value="currCompanyFranchise"
      >
        <SearchBar class="franchise-search-bar" v-model="searchBy.franchiseName" />
        <Option class="franchise-option" v-for="franchise in franchiseList" :key="franchise" :label="franchise" :value="franchise" />
      </Select>
    </template>

    <div class="select-seperator"></div>

    <Select
      @input="handleSelect($event, 'company')"
      :value="filterSelected.company"
      class="company-select"
      :placeholder="$t('company')"
      :disabled="filterOptions.companies.length === 1 || isLoading"
    >
      <SearchBar class="company-search-bar" v-model="searchBy.companyName" />
      <Option
        class="company-option"
        v-for="company in companiesToShow"
        :key="company.id"
        :label="company.name"
        :value="company.id"
      />
    </Select>

    <div class="select-seperator"></div>

    <Select
      class="site-select"
      @input="handleSelect($event, 'site')"
      :value="filterSelected.site"
      :placeholder="$t('all')"
      :disabled="companySitesLen === 1 || isLoading"
    >
      <SearchBar class="site-search-bar" v-model="searchBy.siteName" v-if="companySitesLen > 1" />
      <Option class="site-option" v-for="site in sitesToShow" :key="site.id" :label="site.name" :value="site.id" />
    </Select>

    <div class="select-seperator"></div>

    <div class="rooms-cleaners-details">{{ roomCount }} {{$t('rooms')}}</div>
    <RoomBackButton v-if="$route.params.roomId" />
  </section>
</template>

<script>
import Select from "../../components/Select";
import Option from "../../components/Option";
import SearchBar from "../../components/SearchBar";
import RoomBackButton from '../../../performance/components/RoomBackButton';
import { mapGetters } from 'vuex';

export default {
  name:'CompanySiteFilter',
  data() {
    return {
      searchBy: {
        companyName: '',
        siteName: '',
        franchiseName: ''
      },
      currFranchise: '',
    }
  },
  created() {
    this.currFranchise = this.currCompanyFranchise;
  },
  watch: {
    /**
     * Keep franchise filter in sync when company/site changes outside this component
     * (e.g. Communication deep link commits setFilterSelected). Otherwise companiesToShow
     * still filters by a stale currFranchise and the wrong company/site appears for Buzzz users.
     */
    'filterSelected.company'() {
      this.$nextTick(() => {
        const next = this.currCompanyFranchise;
        if (next !== this.currFranchise) {
          this.currFranchise = next;
        }
      });
    },
  },
  methods: {
    handleSelect(value, field) {
      this.clearSearch()
      this.$emit('input', field, value);
    },
    clearSearch(){
      this.searchBy.companyName = ''
      this.searchBy.siteName = ''
      this.searchBy.franchiseName = ''
    },
    handleFranchiseSelect(value) {
      this.currFranchise = value
      const companies = this.filterOptions.companies.filter(company => company.franchiseName === value)
      this.handleSelect(companies[0].id, 'company')
      this.handleSelect(companies[0].sites[0].id, 'site')
    }
  },
  computed: {
    ...mapGetters(['filterSelected', 'filterOptions', 'loggedUser', 'isLoading']),
    currCompanyLogo() {
      if (!this.filterSelected.company) return null
      let icon;
      try {
        const logoUrl = this.filterOptions.companies.find(c => c.id === this.filterSelected.company).logo;
        if (logoUrl) icon = logoUrl;
      } catch (e) {
        icon = null
      }
      return icon
    },
    roomCount() {
      const rooms = this.filterOptions && this.filterOptions.rooms
      return Array.isArray(rooms) ? rooms.length : 0
    },
    companySitesLen() {
      const { company: companyId } = this.filterSelected;
      const { companies } = this.filterOptions;
      const foundCompany = companies && companies.find(company => company.id === companyId)
      if (foundCompany && Array.isArray(foundCompany.sites)) return foundCompany.sites.length
      else return 1
    },
    sitesToShow() {
      const { company: companyId } = this.filterSelected;
      const { siteName } = this.searchBy
      const foundCompany = this.filterOptions.companies.find(company => company.id === companyId);
      if (foundCompany && Array.isArray(foundCompany.sites)) {
        return foundCompany.sites.filter(site => site.name.toLowerCase().includes(siteName.toLowerCase()))
      }
      return []
    },
    companiesToShow() {
      const { companyName } = this.searchBy
      const { companies } = this.filterOptions
      const filteredCompanies = companies.filter(company => company.name.toLowerCase().includes(companyName.toLowerCase()))
      if (this.isUserLevelBuzzz) {
        return filteredCompanies.filter(company => company.franchiseName === this.currFranchise)
      }
      return filteredCompanies
    },
    franchiseList() {
      const { companies } = this.filterOptions
      let franchiseList = companies.map(company => company.franchiseName)
      if (this.searchBy.franchiseName) {
        franchiseList = franchiseList.filter(franchise => franchise.toLowerCase().includes(this.searchBy.franchiseName.toLowerCase()))
      }
      return [...new Set(franchiseList)]
    },
    currCompanyFranchise() {
      const { company: companyId } = this.filterSelected;
      const { companies } = this.filterOptions;
      const foundCompany = companies.find(company => company.id === companyId)
      if (foundCompany) return foundCompany.franchiseName
      else return ''
    },
    isUserLevelBuzzz() {
      return this.loggedUser.level === 'buzzz'
    }
  },
  components: { Select, Option, RoomBackButton, SearchBar }
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.company-filter {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  position: relative;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;

  .company-logo {
    width: auto;
    height: 28px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    @media (max-width: 1024px) {
      height: 26px;
    }
    @media (max-width: 768px) {
      height: 24px;
    }
    @include screen-size(xs) {
      height: 22px;
    }
  }

  .select-seperator {
    height: 28px;
    border-left: 1px solid #ececec;
    margin: 0 10px 0 12px;
    flex-shrink: 0;
    @media (max-width: 640px) {
      margin: 0 8px 0 8px;
    }
  }

  .company-select, .franchise-select, .site-select {
    max-width: clamp(104px, 12vw, 200px);
    min-width: 96px;
    flex: 0 1 auto;
    @media (max-width: 640px) {
      max-width: clamp(96px, 22vw, 160px);
      min-width: 88px;
    }
  }

  .rooms-cleaners-details {
    letter-spacing: -0.23px;
    width: 100px;
    color: $blue;
    @media (max-width: 600px) {
      display: none;
    }
  }

  .logo {
    height: 100%;
    width: auto;
    max-width: none;
    object-fit: contain;
    display: block;
  }
}

</style>